import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { queryOptions } from "@tanstack/react-query";
import { CalendarDays, Clock, MapPin, Users } from "lucide-react";
import { useState } from "react";

import { DisposicionSitio } from "@/components/medam/sitio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatearFecha } from "@/lib/catalogos";
import { listarPosicionesPublicas } from "@/lib/posiciones.functions";

const posicionesQuery = queryOptions({
  queryKey: ["posiciones", "publicas"],
  queryFn: () => listarPosicionesPublicas(),
});

export const Route = createFileRoute("/posiciones/")({
  head: () => ({
    meta: [
      { title: "Posiciones disponibles en MEDAM" },
      {
        name: "description",
        content:
          "Consulta las posiciones publicadas por MEDAM, filtra por área, turno y tipo de jornada, y postúlate en línea.",
      },
      { property: "og:title", content: "Posiciones disponibles en MEDAM" },
      {
        property: "og:description",
        content: "Explora las vacantes abiertas para el inicio de operaciones de MEDAM.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  loader: ({ context }) => {
    context.queryClient.ensureQueryData(posicionesQuery);
  },
  errorComponent: ({ error }) => (
    <DisposicionSitio>
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-display text-2xl">No pudimos cargar las posiciones</h1>
        <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
      </div>
    </DisposicionSitio>
  ),
  notFoundComponent: () => (
    <DisposicionSitio>
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-display text-2xl">Sin posiciones</h1>
      </div>
    </DisposicionSitio>
  ),
  component: Posiciones,
});

const TODOS = "todos";

function Posiciones() {
  const { data, isLoading, isError, error } = useQuery(posicionesQuery);
  const [area, setArea] = useState(TODOS);
  const [turno, setTurno] = useState(TODOS);
  const [jornada, setJornada] = useState(TODOS);
  const posiciones = data ?? [];

  if (isLoading) {
    return (
      <DisposicionSitio>
        <div className="mx-auto max-w-3xl px-4 py-24 text-center">
          <h1 className="font-display text-2xl">Cargando posiciones...</h1>
        </div>
      </DisposicionSitio>
    );
  }

  if (isError) {
    return (
      <DisposicionSitio>
        <div className="mx-auto max-w-3xl px-4 py-24 text-center">
          <h1 className="font-display text-2xl">No pudimos cargar las posiciones</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {error instanceof Error ? error.message : "Intenta nuevamente en unos momentos."}
          </p>
        </div>
      </DisposicionSitio>
    );
  }

  const unicos = (clave: "area" | "turno" | "tipo_jornada") =>
    Array.from(new Set(posiciones.map((p) => p[clave]).filter(Boolean))) as string[];

  const filtradas = posiciones.filter(
    (p) =>
      (area === TODOS || p.area === area) &&
      (turno === TODOS || p.turno === turno) &&
      (jornada === TODOS || p.tipo_jornada === jornada),
  );

  return (
    <DisposicionSitio>
      <section className="superficie-institucional border-b border-border/60">
        <div className="mx-auto w-full max-w-6xl px-4 py-12 sm:px-6 md:py-16">
          <h1 className="font-display text-3xl sm:text-4xl">Posiciones disponibles</h1>
          <p className="mt-3 max-w-2xl text-muted-foreground">
            Estas son las posiciones publicadas para el inicio de operaciones de MEDAM. Revisa los
            detalles de cada una antes de enviar tu postulación.
          </p>
        </div>
      </section>

      <div className="mx-auto w-full max-w-6xl px-4 py-10 sm:px-6">
        <div className="grid gap-3 sm:grid-cols-3">
          <Filtro etiqueta="Área" valor={area} onChange={setArea} opciones={unicos("area")} />
          <Filtro etiqueta="Turno" valor={turno} onChange={setTurno} opciones={unicos("turno")} />
          <Filtro
            etiqueta="Tipo de jornada"
            valor={jornada}
            onChange={setJornada}
            opciones={unicos("tipo_jornada")}
          />
        </div>

        {filtradas.length === 0 ? (
          <p className="mt-12 rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground">
            Por ahora no hay posiciones que coincidan con los filtros seleccionados.
          </p>
        ) : (
          <ul className="mt-8 grid gap-5 md:grid-cols-2">
            {filtradas.map((posicion) => (
              <li
                key={posicion.id}
                className="flex flex-col rounded-2xl border border-border/70 bg-card p-6 shadow-sm transition-shadow hover:shadow-md"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="secondary">{posicion.area}</Badge>
                  <Badge variant="outline">{posicion.turno}</Badge>
                  {posicion.tipo_jornada ? (
                    <Badge variant="outline">{posicion.tipo_jornada}</Badge>
                  ) : null}
                </div>
                <h2 className="mt-4 font-display text-xl">{posicion.nombre}</h2>
                <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-muted-foreground">
                  {posicion.descripcion || posicion.objetivo}
                </p>
                <dl className="mt-4 grid gap-2 text-sm text-muted-foreground">
                  <Dato icono={Clock} texto={posicion.horario || "Horario por definir"} />
                  <Dato
                    icono={Users}
                    texto={`${posicion.vacantes} ${posicion.vacantes === 1 ? "posición disponible" : "posiciones disponibles"}`}
                  />
                  <Dato
                    icono={CalendarDays}
                    texto={`Fecha límite: ${formatearFecha(posicion.fecha_limite)}`}
                  />
                  {posicion.subarea ? <Dato icono={MapPin} texto={posicion.subarea} /> : null}
                </dl>
                <Button asChild className="mt-6 w-full sm:w-auto sm:self-start">
                  <Link to="/posiciones/$id" params={{ id: posicion.id }}>
                    Conocer la posición
                  </Link>
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </DisposicionSitio>
  );
}

function Dato({
  icono: Icono,
  texto,
}: {
  icono: typeof Clock;
  texto: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <Icono className="size-4 shrink-0 text-primary" aria-hidden="true" />
      <span>{texto}</span>
    </div>
  );
}

function Filtro({
  etiqueta,
  valor,
  onChange,
  opciones,
}: {
  etiqueta: string;
  valor: string;
  onChange: (valor: string) => void;
  opciones: string[];
}) {
  return (
    <label className="block text-sm">
      <span className="mb-1.5 block font-medium text-foreground">{etiqueta}</span>
      <Select value={valor} onValueChange={onChange}>
        <SelectTrigger className="w-full bg-card">
          <SelectValue placeholder={etiqueta} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={TODOS}>Todas</SelectItem>
          {opciones.map((opcion) => (
            <SelectItem key={opcion} value={opcion}>
              {opcion}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </label>
  );
}
